import { useEffect, useState } from 'react';
import { apiFetch } from '../utils/api';
import {
  DEFAULT_MAP_CENTER,
  FALLBACK_COORDINATE_SYSTEMS,
  FALLBACK_OUTPUT_FORMATS,
  toWgs84ForPreview
} from '../utils/coordinateTool';

/**
 * 坐标工具表单状态、坐标系/格式拉取、转换/批量/文件/导出
 * （mapCenter/mapMarker 与转换结果联动，放在本 hook 避免与 map hook 循环依赖）
 */
export default function useCoordinateToolForm() {
  const [inputLat, setInputLat] = useState('');
  const [inputLng, setInputLng] = useState('');
  const [batchInput, setBatchInput] = useState('');
  const [mapUrl, setMapUrl] = useState('');
  const [fromSystem, setFromSystem] = useState('WGS84');
  const [toSystem, setToSystem] = useState('BD09');
  const [outputFormat, setOutputFormat] = useState('DD');
  const [result, setResult] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [mapUrlResult, setMapUrlResult] = useState(null);
  const [file, setFile] = useState(null);
  const [fileResult, setFileResult] = useState(null);
  const [activeTab, setActiveTab] = useState('single'); // single, batch, mapurl, file
  const [loading, setLoading] = useState(false);
  const [coordinateSystems, setCoordinateSystems] = useState([]);
  const [outputFormats, setOutputFormats] = useState([]);
  const [mapCenter, setMapCenter] = useState(DEFAULT_MAP_CENTER);
  const [mapMarker, setMapMarker] = useState(null);
  const fetchCoordinateSystems = async () => {
    try {
      const res = await apiFetch('/api/convert/systems');
      const data = await res.json();
      if (data.success && Array.isArray(data.systems) && data.systems.length) {
        setCoordinateSystems(data.systems);
        return;
      }
    } catch (e) {
      console.error('获取坐标系列表失败:', e);
    }
    setCoordinateSystems(FALLBACK_COORDINATE_SYSTEMS);
  };

  const fetchOutputFormats = async () => {
    try {
      const res = await apiFetch('/api/convert/formats');
      const data = await res.json();
      if (data.success && Array.isArray(data.formats) && data.formats.length) {
        setOutputFormats(data.formats);
        return;
      }
    } catch (e) {
      console.error('获取输出格式失败:', e);
    }
    setOutputFormats(FALLBACK_OUTPUT_FORMATS);
  };

  useEffect(() => {
    fetchCoordinateSystems();
    fetchOutputFormats();
  }, []);

  const handleConvert = async () => {
    if (!inputLat.trim() || !inputLng.trim()) {
      alert('请输入完整的坐标信息');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const res = await apiFetch('/api/convert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coordinates: [{ lat: inputLat, lng: inputLng }],
          from: fromSystem,
          to: toSystem,
          outputFormat: outputFormat
        })
      });
      const data = await res.json();
      if (data.success && data.results && data.results[0]) {
        const convertResult = data.results[0];
        setResult(convertResult);

        if (convertResult.converted && convertResult.converted.lat && convertResult.converted.lng) {
          const convertedCoords = {
            lng: parseFloat(convertResult.converted.lng),
            lat: parseFloat(convertResult.converted.lat)
          };
          // 预览地图是 Mapbox（WGS84）：结果若为 GCJ02/BD09，标记按其 WGS84 位置画，才落在正确地物上
          const display = toWgs84ForPreview(convertedCoords, toSystem);
          setMapCenter([display.lng, display.lat]);
          setMapMarker(display);
        }
      } else {
        setResult({ error: data.error || '转换失败' });
      }
    } catch (e) {
      setResult({ error: '转换失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleBatchConvert = async () => {
    if (!batchInput.trim()) {
      alert('请输入批量坐标数据');
      return;
    }

    setLoading(true);
    setBatchResult(null);

    try {
      const coordinateStrings = batchInput.split('\n').filter(line => line.trim());

      const res = await apiFetch('/api/convert/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coordinateStrings,
          from: fromSystem,
          to: toSystem,
          outputFormat: outputFormat
        })
      });
      const data = await res.json();
      if (data.success) {
        setBatchResult(data);
      } else {
        setBatchResult({ error: data.error || '批量转换失败' });
      }
    } catch (e) {
      setBatchResult({ error: '批量转换失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = e => setFile(e.target.files[0]);

  const handleFileUpload = async () => {
    if (!file) return;

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await apiFetch('/api/parse-file', { method: 'POST', body: formData });
      const data = await res.json();
      setFileResult(data);
    } catch (e) {
      setFileResult({ error: '文件解析失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (coordinates, format, filename) => {
    try {
      const res = await apiFetch('/api/export/coordinates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coordinates,
          format,
          filename
        })
      });

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.${format.toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      alert('导出失败');
    }
  };

  return {
    inputLat,
    setInputLat,
    inputLng,
    setInputLng,
    batchInput,
    setBatchInput,
    mapUrl,
    setMapUrl,
    fromSystem,
    setFromSystem,
    toSystem,
    setToSystem,
    outputFormat,
    setOutputFormat,
    result,
    setResult,
    batchResult,
    mapUrlResult,
    setMapUrlResult,
    file,
    fileResult,
    activeTab,
    setActiveTab,
    loading,
    setLoading,
    coordinateSystems,
    outputFormats,
    handleConvert,
    handleBatchConvert,
    handleFileChange,
    handleFileUpload,
    handleExport,
    mapCenter,
    setMapCenter,
    mapMarker,
    setMapMarker
  };
}
